import { useEffect, useMemo, useState } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

function StaffManagement() {
  const token = localStorage.getItem("token")?.trim();

  const authHeaders = useMemo(
    () => ({
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }),
    [token]
  );

  const [staffList, setStaffList] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const loadStaff = async () => {
    try {
      setIsLoading(true);

      const res = await axios.get(`${API_URL}/auth/staff`, authHeaders);
      setStaffList(res.data || []);
    } catch (error) {
      setMessage(error.response?.data?.message || "Could not load staff.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      loadStaff();
    }
  }, [token]);

  const createStaff = async (e) => {
    e.preventDefault();

    if (!name.trim() || !email.trim() || !password.trim()) {
      setMessage("Name, email and password are required.");
      return;
    }

    try {
      setIsCreating(true);
      setMessage("");

      await axios.post(
        `${API_URL}/auth/staff`,
        {
          name: name.trim(),
          email: email.trim(),
          password,
        },
        authHeaders
      );

      setName("");
      setEmail("");
      setPassword("");
      setMessage("Staff account created successfully.");

      loadStaff();
    } catch (error) {
      setMessage(
        error.response?.data?.message || "Could not create staff account."
      );
    } finally {
      setIsCreating(false);
    }
  };

  const toggleStaffStatus = async (staff) => {
    try {
      setMessage("");

      await axios.patch(
        `${API_URL}/auth/staff/${staff._id}/status`,
        {
          isActive: !staff.isActive,
        },
        authHeaders
      );

      setMessage(
        !staff.isActive
          ? "Staff account activated successfully."
          : "Staff account deactivated successfully."
      );

      loadStaff();
    } catch (error) {
      setMessage(
        error.response?.data?.message || "Could not update staff status."
      );
    }
  };

  const resetPassword = async (staff) => {
    const newPassword = window.prompt(
      `Enter new password for ${staff.name}. Minimum 6 characters.`
    );

    if (!newPassword) return;

    if (newPassword.length < 6) {
      setMessage("Password must be at least 6 characters.");
      return;
    }

    try {
      setMessage("");

      await axios.patch(
        `${API_URL}/auth/staff/${staff._id}/password`,
        {
          password: newPassword,
        },
        authHeaders
      );

      setMessage("Staff password reset successfully.");
    } catch (error) {
      setMessage(
        error.response?.data?.message || "Could not reset staff password."
      );
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Staff Management</h1>
          <p style={styles.subtitle}>
            Create cashier accounts, disable access, and reset staff passwords.
          </p>
        </div>
      </div>

      {message && <p style={styles.message}>{message}</p>}

      <div style={styles.layout}>
        <form onSubmit={createStaff} style={styles.card}>
          <h2>Add New Staff</h2>

          <input
            style={styles.input}
            placeholder="Staff full name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          <input
            style={styles.input}
            placeholder="Staff email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <input
            style={styles.input}
            placeholder="Temporary password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <button type="submit" style={styles.primaryButton} disabled={isCreating}>
            {isCreating ? "Creating..." : "Create Staff"}
          </button>
        </form>

        <div style={styles.card}>
          <h2>Staff Accounts</h2>

          {isLoading ? (
            <p>Loading staff...</p>
          ) : staffList.length === 0 ? (
            <p>No staff accounts created yet.</p>
          ) : (
            <div style={styles.staffList}>
              {staffList.map((staff) => (
                <div key={staff._id} style={styles.staffRow}>
                  <div>
                    <strong>{staff.name}</strong>
                    <p style={styles.smallText}>{staff.email}</p>
                    <span
                      style={{
                        ...styles.status,
                        background: staff.isActive ? "#dcfce7" : "#fee2e2",
                        color: staff.isActive ? "#166534" : "#991b1b",
                      }}
                    >
                      {staff.isActive ? "Active" : "Disabled"}
                    </span>
                  </div>

                  <div style={styles.actions}>
                    <button
                      type="button"
                      onClick={() => toggleStaffStatus(staff)}
                      style={styles.secondaryButton}
                    >
                      {staff.isActive ? "Disable" : "Activate"}
                    </button>

                    <button
                      type="button"
                      onClick={() => resetPassword(staff)}
                      style={styles.secondaryButton}
                    >
                      Reset Password
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    padding: "24px",
    fontFamily: "Arial, sans-serif",
    background: "#f4f6f8",
    minHeight: "100vh",
  },

  header: {
    marginBottom: "20px",
  },

  title: {
    margin: 0,
  },

  subtitle: {
    margin: "6px 0 0",
    color: "#555",
  },

  message: {
    padding: "12px",
    background: "#fff3cd",
    borderRadius: "8px",
    marginBottom: "16px",
  },

  layout: {
    display: "grid",
    gridTemplateColumns: "380px 1fr",
    gap: "20px",
    alignItems: "start",
  },

  card: {
    background: "white",
    padding: "20px",
    borderRadius: "14px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  },

  input: {
    width: "100%",
    padding: "14px",
    marginBottom: "12px",
    fontSize: "16px",
    borderRadius: "8px",
    border: "1px solid #ccc",
    boxSizing: "border-box",
  },

  primaryButton: {
    width: "100%",
    padding: "16px",
    fontSize: "17px",
    borderRadius: "10px",
    border: "none",
    cursor: "pointer",
    fontWeight: "bold",
  },

  staffList: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },

  staffRow: {
    border: "1px solid #eee",
    borderRadius: "12px",
    padding: "16px",
    display: "flex",
    justifyContent: "space-between",
    gap: "16px",
    alignItems: "center",
  },

  smallText: {
    margin: "4px 0 8px",
    color: "#555",
  },

  status: {
    display: "inline-block",
    padding: "5px 10px",
    borderRadius: "999px",
    fontSize: "13px",
    fontWeight: "bold",
  },

  actions: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
  },

  secondaryButton: {
    padding: "10px 12px",
    borderRadius: "8px",
    border: "1px solid #ccc",
    background: "white",
    cursor: "pointer",
  },
};

export default StaffManagement;